// Pallet Dispatch — the pallet desk. Pallet orders (pick queue 5) arrive
// here once every pallet of the order is at the holding area (Pick Face ›
// Pallet Picks). Since 2026-10-21 the desk runs like the normal dispatch
// desk: a landing list, then two full-screen steps per order, then back to
// the list.
//
//   LIST     Release bar (API carriers), the orders by state, the scan
//            prompt, the Auto print toggle (the dispatch preference).
//            Scan any pallet → POST /pallet-dispatch/open → its order opens;
//            tapping a row opens it too.
//   PACK     (components/pallet-dispatch/pallet-pack-screen) — the order's
//            pallets tick off green as each label is scanned.
//            API carrier (Palletways): books on the last scan when nothing
//            needs correcting (single pallet: on the opening scan), else
//            the big button books → POST /pallet-dispatch/book.
//            Manual (DB Schenker) or direct (JAS) carrier at Poland: the
//            big button moves on once every pallet is scanned.
//   CONFIRM  (pallet-confirm-screen) — Palletways: print the label PDF
//            (auto print fires it on arrival), void while unreleased.
//            Manual: tracking fields and "Print label & Ship" → prints the
//            attached label(s) then POST /pallet-dispatch/ship, which marks
//            the order shipped at once and tells the sales channel.
//            Direct (JAS): "Book with JAS and ship" → POST
//            /pallet-dispatch/ship books the shipment at JAS, the label
//            PDF it returns prints here, the waybill PDF is a button, and
//            the order is marked shipped at once.
//            Done → LIST.
//
// Booked / released orders open straight on CONFIRM (reprint, void) —
// there is nothing to scan. A pallet of a DIFFERENT order scanned while a
// screen is open is refused: back to the list first.
//
// Release (API carriers only): before collection, releases the day's
// booked consignments → POST /pallet-dispatch/release. Irreversible. Each
// order is marked shipped as soon as Palletways issue its tracking number,
// which can lag the release — the list picks those up as it refreshes.
//
// Printing: every pallet document is a file (PDF label, PDF / image
// attachments), printed through the browser's print dialog
// (helpers/carrierLabelPrint.printPdfBase64) — the print agent takes HTML
// or ZPL only, so there is no printer pill here. If no dialog opens the
// PDF is downloaded instead.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Printer, Send } from "lucide-react";
import { toast } from "react-toastify";
import { usePageTitle } from "../hooks/usePageTitle";
import { useTranslation } from "../i18n";
import { apiFetch } from "../api/apiFetch";
import { playSuccess, playError } from "../components/helpers/audioPlayer";
import HiddenScanInput from "../components/HiddenScanInput";
import { useReadOnly } from "../access/ReadOnly";
import { getAutoPrint, setAutoPrintStorage } from "../components/dispatch/dispatchSession";
import PalletPackScreen, { type ScanFx } from "../components/pallet-dispatch/pallet-pack-screen";
import PalletConfirmScreen, { type ConfirmState, type DeskConfirm, type ShipResult } from "../components/pallet-dispatch/pallet-confirm-screen";
import { printPdf, palletwaysFilename, directFilename, printAttachment } from "../components/pallet-dispatch/palletPrint";
import {
    LOADING_LIST_CARRIERS, carrierOptionsOf, deskState, isDirectOrder, isShipOrder, palletOfScan, todayWarsaw, withChosenCarrier,
    type DeskData, type DeskDelivery, type DeskDocument, type DeskOrder, type Overrides,
} from "../components/pallet-dispatch/palletDesk";
import "../styles/dispatch.css";
import "../styles/pallet-dispatch.css";

export {
    PALLETWAYS_TYPES, deskState, isManualOrder, phoneDisplay, palletOfScan,
    type DeskPallet, type DeskBooking, type DeskCarrier, type DeskAttachment, type DeskOrder,
} from "../components/pallet-dispatch/palletDesk";

type DeskView = "list" | "pack" | "confirm";

const REFRESH_MS = 15_000;

// Refusal codes that have their own message.
const CODE_KEYS = new Set([
    "unknown_pallet", "scan_all_pallets", "service_limits", "carrier_unavailable", "booking_in_progress",
    "already_released", "no_service", "carrier_rejected", "carrier_unreachable", "label_fetch_failed",
    "order_not_at_desk", "not_all_staged", "no_booking", "carrier_refused",
    "no_pallet_carrier", "use_api_booking", "bad_tracking_url", "fulfil_failed", "not_this_order",
    "carrier_misconfigured", "tracking_required", "carrier_not_allowed", "carrier_not_supported",
]);

const PalletDispatch = () => {
    usePageTitle("pallet_dispatch");
    const navigate = useNavigate();
    const { t } = useTranslation();
    const readOnly = useReadOnly();

    const [data, setData] = useState<DeskData | null>(null);
    const [loadError, setLoadError] = useState(false);
    const [view, setView] = useState<DeskView>("list");
    const [openId, setOpenId] = useState<string | null>(null);
    // The order as /open or the list last gave it — what the screens show
    // once it has left the list (a manual order after Ship).
    const [snapshot, setSnapshot] = useState<DeskOrder | null>(null);
    const [scanned, setScanned] = useState<Set<string>>(new Set());
    const [overrides, setOverrides] = useState<Overrides>({});
    const [scanFx, setScanFx] = useState<ScanFx>({ n: 0, sku: null, ok: true });
    const [scanValue, setScanValue] = useState("");
    const [busy, setBusy] = useState(false);
    const [confirm, setConfirm] = useState<"release" | DeskConfirm>(null);
    const [autoPrint, setAutoPrint] = useState(getAutoPrint());
    // The label that came back from the booking we just made, printed on
    // the confirm screen (so the reprint endpoint is not asked for it).
    const [justBooked, setJustBooked] = useState<{ order_id: string; label: { contentBase64?: string }; con_no: string | null } | null>(null);
    // Manual-carrier panel: what the operator types before Ship, and what
    // Ship answered.
    const [tracking, setTracking] = useState("");
    const [trackingUrl, setTrackingUrl] = useState("");
    const [shipResult, setShipResult] = useState<(ShipResult & { order_id: string }) | null>(null);
    // Several pallet carriers can serve one country (JAS and Raben at Poland):
    // the operator's pick for the open order; null = the order's own default.
    const [carrierChoice, setCarrierChoice] = useState<string | null>(null);
    // Loading list (lista załadunkowa): the day it is printed for.
    const [loadingListDate, setLoadingListDate] = useState(todayWarsaw);
    const mountedRef = useRef(true);

    const announceShipped = useCallback((shipped: unknown) => {
        if (!Array.isArray(shipped)) return;
        for (const s of shipped) {
            if (s?.fulfilled) {
                toast.success(t("pallet_dispatch_shipped", { order: String(s.order_id), tracking: String(s.tracking_id ?? "") }), { toastId: `pallet-shipped-${s.order_id}` });
            } else if (s?.error) {
                toast.error(t("pallet_dispatch_ship_failed", { order: String(s.order_id), error: String(s.error) }), { toastId: `pallet-ship-failed-${s.order_id}` });
            }
        }
    }, [t]);

    const load = useCallback(async () => {
        try {
            const res = await apiFetch("/pallet-dispatch", {});
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const json = await res.json();
            if (!mountedRef.current) return;
            setData({
                enabled: json.enabled === true,
                api_ready: json.api_ready !== false,
                carriers: Array.isArray(json.carriers) ? json.carriers : [],
                orders: Array.isArray(json.orders) ? json.orders : [],
            });
            setLoadError(false);
            announceShipped(json.shipped);
        } catch {
            if (mountedRef.current) setLoadError(true);
        }
    }, [announceShipped]);

    useEffect(() => {
        mountedRef.current = true;
        load();
        const timer = window.setInterval(load, REFRESH_MS);
        return () => {
            mountedRef.current = false;
            window.clearInterval(timer);
        };
    }, [load]);

    const orders = useMemo(() => data?.orders ?? [], [data]);
    const loadingListCarriers = (data?.carriers ?? []).filter((c) => LOADING_LIST_CARRIERS.includes(c.code));
    const hasApiCarrier = (data?.carriers ?? []).some((c) => c.mode === "api");
    // The list's copy wins while the order is on it (booking state moves
    // there); the snapshot carries it after it has gone, and carries a
    // booking the list has not caught up with yet.
    const listCopy = openId ? orders.find((o) => o.order_id === openId) ?? null : null;
    const baseOrder = listCopy && !(snapshot?.booking && !listCopy.booking) ? listCopy : snapshot;
    const openOrder = baseOrder ? withChosenCarrier(baseOrder, carrierChoice) : null;
    const booked = orders.filter((o) => deskState(o) === "booked");
    const released = orders.filter((o) => deskState(o) === "released");
    const ready = orders.filter((o) => deskState(o) === "ready" || deskState(o) === "booking");

    const fail = (code: unknown, json: Record<string, unknown> = {}) => {
        playError();
        const key = typeof code === "string" && CODE_KEYS.has(code) ? `pallet_dispatch_err_${code}` : "pallet_dispatch_err_generic";
        const detail = typeof json.error === "string" ? json.error : "";
        // The carrier-flavoured messages name the order's carrier (JAS, DB
        // Schenker, Palletways) — the desk serves more than Palletways now.
        const fromJson = json.carrier && typeof json.carrier === "object" ? (json.carrier as { name?: unknown }).name : undefined;
        const carrier = String(fromJson ?? openOrder?.carrier?.name ?? data?.carriers?.[0]?.name ?? "");
        // carrier_not_allowed lists the carriers the BE would accept.
        const byCode = new Map((data?.carriers ?? []).map((c) => [c.code, c.name]));
        const allowed = Array.isArray(json.allowed) ? json.allowed.map((c) => byCode.get(String(c)) ?? String(c)).join(", ") : "";
        toast.error(`${t(key, { carrier, allowed })}${detail ? ` — ${detail}` : ""}`, { toastId: `pallet-dispatch-${String(code)}`, autoClose: 10000 });
    };

    const post = async (path: string, body: unknown) => {
        const res = await apiFetch(path, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
        const json = await res.json().catch(() => ({}));
        return { ok: res.ok && json?.success === true, json };
    };

    // One request at a time.
    const run = async (action: () => Promise<void>) => {
        if (busy) return;
        setBusy(true);
        try {
            await action();
        } catch {
            if (mountedRef.current) fail("generic");
        } finally {
            if (mountedRef.current) {
                setBusy(false);
                setConfirm(null);
            }
        }
    };

    const toggleAutoPrint = () => setAutoPrint((prev) => { setAutoPrintStorage(!prev); return !prev; });

    // ── Opening and leaving an order ─────────────────────────────────────

    const openWith = (order: DeskOrder, scannedSku: string | null) => {
        setOpenId(order.order_id);
        setSnapshot(order);
        setOverrides({});
        setCarrierChoice(null);
        setTracking("");
        setTrackingUrl("");
        setConfirm(null);
        setJustBooked(null);
        setShipResult(null);
        setScanned(new Set(scannedSku ? [scannedSku] : []));
        setScanFx({ n: 0, sku: null, ok: true });
        // Nothing to scan on a booked / released order: straight to confirm.
        setView(deskState(order) === "ready" ? "pack" : "confirm");
    };

    const backToList = async () => {
        setView("list");
        setOpenId(null);
        setSnapshot(null);
        setScanned(new Set());
        setOverrides({});
        setCarrierChoice(null);
        setJustBooked(null);
        setShipResult(null);
        setConfirm(null);
        await load();
    };

    // The delivery address corrected on the open order (saved by the
    // screen): the list's copy wins over the snapshot, so both are updated
    // and the screen shows the new address before the next load catches up.
    const updateDelivery = (orderId: string, delivery: DeskDelivery) => {
        setSnapshot((s) => (s && s.order_id === orderId ? { ...s, delivery } : s));
        setData((d) => (d ? { ...d, orders: d.orders.map((o) => (o.order_id === orderId ? { ...o, delivery } : o)) } : d));
    };

    // ── Palletways ───────────────────────────────────────────────────────

    // Book `order`. `scannedSkus` is passed explicitly because a scan that
    // completes the set books in the same tick as it is recorded. Success
    // moves on to the confirm screen, where the label prints.
    const bookOrder = async (order: DeskOrder, scannedSkus: string[]) => {
        const { ok, json } = await post("/pallet-dispatch/book", {
            order_id: order.order_id, scanned_skus: scannedSkus, overrides,
        });
        if (!ok) { fail(json?.code, json); await load(); return; }
        playSuccess();
        toast.success(t(json.already ? "pallet_dispatch_reprinted" : "pallet_dispatch_booked", { order: order.order_number ?? order.order_id }), { toastId: `pallet-booked-${order.order_id}` });
        if (json.label?.contentBase64) setJustBooked({ order_id: order.order_id, label: json.label, con_no: json.booking?.con_no ?? null });
        if (json.booking) setSnapshot({ ...order, booking: json.booking });
        setView("confirm");
        await load();
    };

    const canAutoBook = (order: DeskOrder) =>
        deskState(order) === "ready" && order.problems.length === 0 && !readOnly && !isShipOrder(order) && order.carrier != null;

    // The stored documents of a booked order (Palletways label; JAS label +
    // waybill), for a reprint.
    const fetchDocuments = async (order: DeskOrder): Promise<{ label?: DeskDocument | null; waybill?: DeskDocument | null } | null> => {
        const res = await apiFetch(`/pallet-dispatch/label/${encodeURIComponent(order.order_id)}`, {});
        const json = await res.json().catch(() => ({}));
        if (!res.ok || !json?.label) { fail(json?.code ?? "no_booking", json); return null; }
        return json;
    };

    const printLabels = (order: DeskOrder) => run(async () => {
        if (justBooked && justBooked.order_id === order.order_id) {
            await printPdf(justBooked.label, palletwaysFilename(justBooked.con_no), t);
            return;
        }
        const direct = isDirectOrder(order);
        const ref = shipResult?.tracking_numbers?.[0] ?? order.booking?.waybill_no ?? null;
        if (direct && shipResult?.order_id === order.order_id && shipResult.label?.contentBase64) {
            await printPdf(shipResult.label, directFilename(order.carrier?.name ?? "", ref), t);
            return;
        }
        const docs = await fetchDocuments(order);
        if (!docs) return;
        await printPdf(docs.label, direct ? directFilename(order.carrier?.name ?? "", ref) : palletwaysFilename(order.booking?.con_no ?? null), t);
    });

    // Direct carrier: the waybill (consignment note) that came back with the booking.
    const printWaybill = (order: DeskOrder) => run(async () => {
        const ref = shipResult?.tracking_numbers?.[0] ?? order.booking?.waybill_no ?? null;
        const name = directFilename(order.carrier?.name ?? "", ref, "waybill");
        if (shipResult?.order_id === order.order_id && shipResult.waybill?.contentBase64) {
            await printPdf(shipResult.waybill, name, t);
            return;
        }
        const docs = await fetchDocuments(order);
        if (!docs) return;
        if (!docs.waybill?.contentBase64) { fail("no_booking"); return; }
        await printPdf(docs.waybill, name, t);
    });

    const voidBooking = (order: DeskOrder) => run(async () => {
        const { ok, json } = await post("/pallet-dispatch/void", { order_id: order.order_id });
        if (!ok) { fail(json?.code, json); await load(); return; }
        toast.info(t("pallet_dispatch_voided", { order: order.order_number ?? order.order_id }), { toastId: `pallet-voided-${order.order_id}` });
        await backToList();
    });

    const releaseAll = () => run(async () => {
        const { ok, json } = await post("/pallet-dispatch/release", {});
        if (!ok) { fail(json?.code, json); return; }
        const done = Array.isArray(json.released) ? json.released.length : 0;
        const failed = Array.isArray(json.failed) ? json.failed : [];
        if (done > 0) {
            playSuccess();
            toast.success(t("pallet_dispatch_released", { n: String(done) }), { toastId: "pallet-released" });
        }
        for (const f of failed) {
            toast.error(t("pallet_dispatch_release_failed", { order: String(f.con_no ?? f.order_id), error: String(f.error ?? "") }), { toastId: `pallet-release-failed-${f.booking_id}`, autoClose: false });
        }
        if (done === 0 && failed.length === 0) toast.info(t("pallet_dispatch_nothing_to_release"), { toastId: "pallet-released" });
        announceShipped(json.shipped);
        await load();
    });

    // Loading list (lista załadunkowa) — the document the carrier's driver
    // signs; Raben's API does not produce it, the BE builds it from the day's
    // shipments. Fetched as a PDF and printed like any other desk document.
    const printLoadingList = (carrier: string) => run(async () => {
        const res = await apiFetch(`/pallet-dispatch/loading-list?carrier=${encodeURIComponent(carrier)}&date=${encodeURIComponent(loadingListDate)}`, {});
        const json = await res.json().catch(() => ({}));
        if (res.status === 404 && json?.code === "no_shipments") {
            playError();
            toast.info(t("pallet_dispatch_loading_list_none", { date: loadingListDate }), { toastId: "pallet-loading-list-none" });
            return;
        }
        if (!res.ok || !json?.pdf?.contentBase64) { fail(json?.code, json); return; }
        playSuccess();
        await printPdf(json.pdf, `loading-list-${carrier}-${loadingListDate}.pdf`, t);
        toast.success(t("pallet_dispatch_loading_list_printed", { number: String(json.number ?? ""), n: String(json.shipments ?? "") }), { toastId: "pallet-loading-list" });
    });

    // ── Manual pallet carriers (DB Schenker / JAS) ──────────────────────

    // Print every attached shipping label (PDFs through the print dialog,
    // images in a tab). A label that cannot be fetched is reported and the
    // rest still print.
    const printAttachedLabels = async (order: DeskOrder) => {
        for (const att of (order.attachments ?? []).filter((a) => a.kind === "shipping_label")) {
            const r = await printAttachment(order.order_id, att, t);
            if (!r.ok) fail(r.error);
        }
    };

    // Ship from the confirm screen, behind a confirm. Manual carrier: the
    // attached label(s) print first, as dispatch prints before it ships;
    // then the order is marked shipped at once and the sales channel told.
    // Direct carrier (JAS): the BE books the shipment and answers with the
    // label and the waybill; the label prints at once, the waybill is a
    // button (two print dialogs in a row would fight each other).
    const shipOrder = (order: DeskOrder, scannedSkus: string[]) => run(async () => {
        const direct = isDirectOrder(order);
        if (!direct) await printAttachedLabels(order);
        // carrier_code only when the operator had a choice; the BE refuses a
        // carrier that does not serve the country (carrier_not_allowed).
        const choice = carrierOptionsOf(order).length > 1 && order.carrier ? { carrier_code: order.carrier.code } : {};
        const { ok, json } = await post("/pallet-dispatch/ship", direct
            ? { order_id: order.order_id, scanned_skus: scannedSkus, overrides, ...choice }
            : {
                order_id: order.order_id,
                scanned_skus: scannedSkus,
                tracking_number: tracking.trim() || null,
                tracking_url: trackingUrl.trim() || null,
                ...choice,
            });
        if (!ok) { fail(json?.code, json); await load(); return; }
        playSuccess();
        const carrier = String(json.carrier?.name ?? order.carrier?.name ?? "");
        const trackingNumbers: string[] = Array.isArray(json.tracking_numbers) ? json.tracking_numbers.map(String) : [];
        if (direct) {
            toast.success(t("pallet_dispatch_shipped_direct", { order: order.order_number ?? order.order_id, carrier, tracking: trackingNumbers[0] ?? "" }), { toastId: `pallet-shipped-${order.order_id}` });
            setShipResult({ order_id: order.order_id, carrier, tracking_numbers: trackingNumbers, label: json.label ?? null, waybill: json.waybill ?? null, booking: json.booking ?? null });
            // Raben: the eWZ upload is best effort — the shipment stands, say so.
            if (json.booking?.wz && !json.booking.wz.uploaded && json.booking.wz.error) {
                toast.warn(t("pallet_dispatch_wz_upload_failed", { error: String(json.booking.wz.error) }), { toastId: `pallet-wz-${order.order_id}`, autoClose: 10000 });
            }
            if (json.label?.contentBase64) await printPdf(json.label, directFilename(carrier, trackingNumbers[0] ?? null), t);
        } else {
            toast.success(t("pallet_dispatch_shipped_manual", { order: order.order_number ?? order.order_id, carrier }), { toastId: `pallet-shipped-${order.order_id}` });
            setShipResult({ order_id: order.order_id, carrier, tracking_numbers: trackingNumbers });
        }
        await load();
    });

    // ── Scans ────────────────────────────────────────────────────────────

    const doSubmit = (scannedValue?: string) => {
        const val = (scannedValue ?? scanValue).trim();
        setScanValue("");
        if (!val || !data) return;
        run(async () => {
            if (view !== "list" && openOrder) {
                const here = palletOfScan(openOrder, val);
                if (!here) {
                    // Another order's pallet (or anything else): back to the
                    // list first — switching orders under the operator's feet
                    // on a full-screen flow would lose them.
                    setScanFx((f) => ({ n: f.n + 1, sku: null, ok: false }));
                    fail("not_this_order");
                    return;
                }
                playSuccess();
                setScanFx((f) => ({ n: f.n + 1, sku: here.sku, ok: true }));
                if (view !== "pack") return;
                const next = new Set(scanned).add(here.sku);
                setScanned(next);
                const all = openOrder.pallets.every((p) => next.has(p.sku));
                // A manual-carrier order never ships on a scan: the operator
                // moves on with the big button and ships from the confirm screen.
                if (all && canAutoBook(openOrder)) await bookOrder(openOrder, [...next]);
                return;
            }
            const { ok, json } = await post("/pallet-dispatch/open", { barcode: val });
            if (!ok) { fail(json?.code, json); return; }
            const order = json.order as DeskOrder;
            playSuccess();
            openWith(order, json.scanned_sku ?? null);
            // Make sure the list holds the order we just opened.
            setData((d) => (d && !d.orders.some((o) => o.order_id === order.order_id) ? { ...d, orders: [...d.orders, order] } : d));
            // A single-pallet order with nothing to correct books on the scan
            // (API carrier only — a manual one waits for its label and Ship).
            if (order.pallets.length === 1 && canAutoBook(order)) await bookOrder(order, [order.pallets[0].sku]);
        });
    };

    const renderRow = (order: DeskOrder) => (
        <button
            key={order.order_id}
            type="button"
            className="pallet-dispatch-row"
            onClick={() => openWith(order, null)}
            disabled={busy}
        >
            <span className="pallet-dispatch-row-number">{order.order_number ?? order.order_id}</span>
            <span className="pallet-dispatch-row-meta">
                {t("pallet_dispatch_row_meta", { n: String(order.pallets.length), kg: String(order.total_kg) })}
                {order.delivery.postcode ? ` · ${order.delivery.postcode}` : ""}
            </span>
            {order.booking?.state === "failed" && <span className="pallet-dispatch-chip pallet-dispatch-chip--failed">{t("pallet_dispatch_state_failed")}</span>}
        </button>
    );

    // A desk with no API carrier (Poland) ships, it does not "book".
    const readyHeading = data && !hasApiCarrier ? t("pallet_dispatch_ready_manual") : t("pallet_dispatch_ready");

    // ── Screens ──────────────────────────────────────────────────────────

    if (view === "pack" && openOrder) {
        const scannedSkus = [...scanned];
        return (
            <>
                <PalletPackScreen
                    order={openOrder}
                    state={deskState(openOrder)}
                    scanned={scanned}
                    overrides={overrides}
                    onOverride={(sku, patch) => setOverrides((curr) => ({ ...curr, [sku]: { ...curr[sku], ...patch } }))}
                    scanFx={scanFx}
                    busy={busy}
                    readOnly={readOnly}
                    onCarrierChange={setCarrierChoice}
                    onPrimary={() => {
                        if (isShipOrder(openOrder)) { setView("confirm"); return; }
                        run(() => bookOrder(openOrder, scannedSkus));
                    }}
                    onLeave={backToList}
                    onDelivery={(delivery) => updateDelivery(openOrder.order_id, delivery)}
                    run={run}
                    fail={fail}
                    onChanged={load}
                />
                <HiddenScanInput value={scanValue} onChange={setScanValue} onSubmit={doSubmit} disabled={busy || !data} disableCamera />
            </>
        );
    }

    if (view === "confirm" && openOrder) {
        const state: ConfirmState = shipResult?.order_id === openOrder.order_id ? "shipped" : deskState(openOrder);
        return (
            <>
                <PalletConfirmScreen
                    key={openOrder.order_id}
                    order={openOrder}
                    state={state}
                    busy={busy}
                    readOnly={readOnly}
                    autoPrint={autoPrint}
                    shipResult={shipResult?.order_id === openOrder.order_id ? shipResult : null}
                    tracking={tracking}
                    trackingUrl={trackingUrl}
                    onTracking={setTracking}
                    onTrackingUrl={setTrackingUrl}
                    confirm={confirm === "release" ? null : confirm}
                    onConfirm={setConfirm}
                    onPrintLabels={() => printLabels(openOrder)}
                    onPrintWaybill={() => printWaybill(openOrder)}
                    onPrintAttachedLabels={() => run(() => printAttachedLabels(openOrder))}
                    onShip={() => shipOrder(openOrder, [...scanned])}
                    onCarrierChange={setCarrierChoice}
                    onVoid={() => voidBooking(openOrder)}
                    onDone={backToList}
                    onDelivery={(delivery) => updateDelivery(openOrder.order_id, delivery)}
                    run={run}
                    fail={fail}
                    onChanged={load}
                />
                <HiddenScanInput value={scanValue} onChange={setScanValue} onSubmit={doSubmit} disabled={busy || !data} disableCamera />
            </>
        );
    }

    return (
        <div className="pallet-dispatch">
            <div className="page-title-bar">
                <h1 className="page-title">{t("pallet_dispatch")}</h1>
                <button type="button" className="page-back-btn" onClick={() => navigate("/dispatch/menu")} aria-label={t("back")}>
                    <ArrowLeft size={20} />
                </button>
            </div>

            {data && !data.enabled && <p className="pallet-dispatch-note pallet-dispatch-note--warn">{t("pallet_dispatch_no_carrier")}</p>}
            {data && data.enabled && hasApiCarrier && !data.api_ready && (
                <p className="pallet-dispatch-note pallet-dispatch-note--warn">{t("pallet_dispatch_not_enabled")}</p>
            )}
            {loadError && <p className="pallet-dispatch-note pallet-dispatch-note--warn">{t("server_error")}</p>}

            {/* Release only exists for an API carrier (Palletways manifests);
                a manual-carrier desk (Poland) has nothing to release. */}
            {(!data || hasApiCarrier) && (
                <div className="pallet-dispatch-release">
                    <span>{t("pallet_dispatch_awaiting_release", { n: String(booked.length) })}</span>
                    {confirm !== "release" ? (
                        <button type="button" className="btn btn-primary" disabled={busy || readOnly || booked.length === 0} onClick={() => setConfirm("release")}>
                            <Send size={16} /> {t("pallet_dispatch_release")}
                        </button>
                    ) : (
                        <span className="pallet-dispatch-confirm">
                            <span>{t("pallet_dispatch_release_confirm_text", { n: String(booked.length) })}</span>
                            <button type="button" className="btn btn-danger" disabled={busy || readOnly} onClick={releaseAll}>
                                {t("pallet_dispatch_release_confirm")}
                            </button>
                            <button type="button" className="btn" disabled={busy} onClick={() => setConfirm(null)}>{t("cancel")}</button>
                        </span>
                    )}
                </div>
            )}

            {/* Loading list: one per carrier that has one (Raben). */}
            {loadingListCarriers.map((c) => (
                <div className="pallet-dispatch-loading-list" key={c.code}>
                    <span>{t("pallet_dispatch_loading_list", { carrier: c.name })}</span>
                    <label>
                        <span>{t("pallet_dispatch_loading_list_date")}</span>
                        <input type="date" value={loadingListDate} disabled={busy} onChange={(e) => setLoadingListDate(e.target.value)} />
                    </label>
                    <button type="button" className="btn btn-secondary" disabled={busy || !loadingListDate} onClick={() => printLoadingList(c.code)}>
                        <Printer size={16} /> {t("pallet_dispatch_loading_list_print")}
                    </button>
                </div>
            ))}

            <div className="pallet-dispatch-body">
                <div className="pallet-dispatch-lists">
                    <section aria-label={readyHeading}>
                        <h2>{readyHeading} <span>{ready.length}</span></h2>
                        {ready.length === 0 && <p className="pallet-dispatch-empty">{t("pallet_dispatch_ready_empty")}</p>}
                        {ready.map(renderRow)}
                    </section>
                    <section aria-label={t("pallet_dispatch_booked_heading")}>
                        <h2>{t("pallet_dispatch_booked_heading")} <span>{booked.length}</span></h2>
                        {booked.map(renderRow)}
                    </section>
                    {released.length > 0 && (
                        <section aria-label={t("pallet_dispatch_released_heading")}>
                            <h2>{t("pallet_dispatch_released_heading")} <span>{released.length}</span></h2>
                            <p className="pallet-dispatch-empty">{t("pallet_dispatch_released_hint")}</p>
                            {released.map(renderRow)}
                        </section>
                    )}
                </div>

                <div className="pallet-dispatch-panel" aria-label={t("pallet_dispatch_order")}>
                    <p className="pallet-dispatch-prompt">{t("pallet_dispatch_scan_prompt")}</p>
                </div>
            </div>

            {/* Auto print: the confirm screen prints the label the moment it
                opens. The dispatch desks' preference, shared. */}
            <div className="dispatch-bottom-bar">
                <div className="dispatch-auto-print-toggle" onClick={toggleAutoPrint} role="switch" aria-checked={autoPrint} aria-label={t("auto_print")}>
                    <Printer size={18} />
                    <span>{t("auto_print")}</span>
                    <div className={`dispatch-toggle ${autoPrint ? "dispatch-toggle--on" : ""}`}>
                        <div className="dispatch-toggle-knob" />
                    </div>
                </div>
            </div>

            <HiddenScanInput
                value={scanValue}
                onChange={setScanValue}
                onSubmit={doSubmit}
                disabled={busy || !data}
                disableCamera
            />
        </div>
    );
};

export default PalletDispatch;
